import { Form, Formik, FormikProps } from 'formik';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';

import { Typography } from '@mui/material';
import { FormPart } from '@modules/shared/components/FormPart';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { Modal } from '@shared/components/modals/Modal';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { TextInputField } from '@shared/fields/TextInputField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { IEvent } from '@shared/inputs/NumberInput';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { ModalWrapper } from '@styles/ContactManager/FinanaceFormStyles';

import { SERVICES } from '@shared/constants/apiConfig';
import { IDeal, IEditRetail } from '../../interfaces';
import { productRetailValidationSchema } from './constants';
import { requestUpdateProduct } from './queries';
import { Plans, ProductPlan } from './interfaces';

interface IRetailProps {
  editProduct: Partial<IEditRetail> | null;
  dealId: string;
  isEditRetailModalOpen: number;
  closeModal: () => void;
  reloadDeal: KeyedMutator<IDeal>;
  contactState?: string | null;
}

const handleChange = async (event: IEvent, props: FormikProps<IEditRetail>) => {
  await props.setFieldValue?.('retail', event.target.value === 0 ? 0 : event.target.value || null);
  props.setFieldValue?.('markup', event.target.value - props?.values?.cost);
};

const formConfig = (props: FormikProps<IEditRetail>) => [
  {
    width: '100%',
    name: 'cost',
    prefix: '$',
    disabled: true,
    component: NumberInputField,
  },
  {
    width: '100%',
    name: 'markup',
    prefix: props.values.markup < 0 ? '-$' : '$',
    disabled: true,
    decimalScale: 2,
    component: NumberInputField,
  },
  {
    width: '100%',
    name: 'retail',
    prefix: '$',
    component: NumberInputField,
    decimalScale: 2,
    onChange: (target: IEvent) => handleChange(target, props),
  },
  {
    width: '100%',
    name: 'reason',
    label: 'Please provide a reason to update',
    multiline: true,
    maxRows: 5,
    component: TextInputField,
  },
];

export const EditRetailModal = ({ editProduct, isEditRetailModalOpen, dealId, closeModal, reloadDeal, contactState }: IRetailProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [pendingData, setPendingData] = useState<Partial<IEditRetail> | null>(null);
  const [productPlanRule, setProductPlanRule] = useState<ProductPlan>();

  const { data } = useSWR<Plans>(`/${SERVICES.PRODUCT}/deals/${dealId}/plans?newVersion=false&&state=${contactState}`, {
    revalidateOnMount: true,
  });

  useEffect(() => {
    const productPlan = data?.productPlans?.find((plan) =>
      plan.productName === editProduct?.productTypeName
    );
    setProductPlanRule(productPlan);
  }, [data, editProduct?.productTypeName]);

  const processSave = useCallback(async (data: Partial<IEditRetail>) => {
    setLoading(true);
    try {
      await requestUpdateProduct(data, dealId);
      reloadDeal?.();
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      closeModal();
    } finally {
      setLoading(false);
    }
  }, [dealId, reloadDeal, dispatch, closeModal]);

  const handleSubmit = useCallback(async (data: Partial<IEditRetail>) => {
    if (productPlanRule && productPlanRule?.providers?.[0]?.maxAllowedRetail && data.retail && data.retail > productPlanRule.providers[0].maxAllowedRetail) {
      setPendingData(data);
      setOpenAlertModal(true);
      return;
    }
    
    await processSave(data);
  }, [productPlanRule, processSave]);

  const confirmationOfRetailMaxAlert = useCallback(async () => {
    setOpenAlertModal(false);
    if (pendingData) {
      await processSave(pendingData);
      setPendingData(null);
    }
  }, [pendingData, processSave]);

  return (
    <>
      <Modal
        hideBackdrop
        title="Edit Retail"
        dialogWidth={'500px'}
        open={!!isEditRetailModalOpen}
        resetPosition={isEditRetailModalOpen}
        onClose={closeModal}
      >
        <Formik
          enableReinitialize
          initialValues={{ ...editProduct }}
          validationSchema={productRetailValidationSchema}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit}>
              <ModalWrapper>
                {(productPlanRule && productPlanRule.productTypeRuleDescription.length > 0) && (
                  <Typography style={{ marginBottom: '8px', fontSize: '14px', color: 'black' }}>
                    {productPlanRule.productTypeRuleDescription}
                  </Typography>
                )}
                <FormPart config={formConfig(props as FormikProps<IEditRetail>) as IFormFieldConfig<typeof props>[]} />
                <CommonButtonWrapper direction="row">
                  <CancelButton onClick={closeModal} />
                  <SaveButton isLoading={loading} />
                </CommonButtonWrapper>
              </ModalWrapper>
            </Form>
          )}
        </Formik>
      </Modal>
      <AlertModal
        title="Alert"
        textSize="20px"
        subtitle={`The retail value you have entered exceeds the maximum allowed retail of $${productPlanRule?.providers?.[0]?.maxAllowedRetail}. Do you want to proceed?`}
        cancelButtonText="Cancel"
        buttonText="Proceed"
        marginTop={0}
        open={openAlertModal}
        onConfirm={confirmationOfRetailMaxAlert}
        onClose={() => {
          setOpenAlertModal(false);
          setPendingData(null);
        }}
      />
    </>
  );
};